import React from "react";
import PropTypes from "prop-types";
import { Input } from "reactstrap";
import { ROLES, RbacProvider, useRbac } from "./RbacContext";
import { isReadOnlyMode } from "../../../helpers/readOnlyMode";

export { ROLES, RbacProvider, useRbac };
export {
  PERM_MODULES, PERM_LEVELS, PERM_FIELD_MASKS,
  capabilityMatrix, fieldMaskMatrix, isBuiltInRole,
} from "./RbacContext";

/**
 * 依角色/能力隱藏內容（主計劃 P0-5）。
 *
 *   <RbacGuard perm="user.create"><Button>新增</Button></RbacGuard>
 *   <RbacGuard allow={["Gatekeeper","Overseer"]}>...</RbacGuard>
 *
 * 不符權限時渲染 fallback（預設不顯示）。
 */
export const RbacGuard = ({ allow, perm, fallback = null, children }) => {
  const { role, can } = useRbac();
  const okByRole = allow ? allow.includes(role) : false;
  const okByPerm = perm ? can(perm) : false;
  const allowed = (allow && okByRole) || (perm && okByPerm) || (!allow && !perm);
  return allowed ? <>{children}</> : fallback;
};
RbacGuard.propTypes = {
  allow: PropTypes.arrayOf(PropTypes.string),
  perm: PropTypes.string,
  fallback: PropTypes.node,
  children: PropTypes.node,
};

/**
 * UAT 唯讀模式的「寫入入口」包裝(helpers/readOnlyMode)。
 *
 * 給那些**刻意不依角色藏**的裸按鈕用 —— 新增／批次匯入原本「人人可發動,仍走審核」
 * (裁示 8/13),所以沒有 perm 可以掛 RbacGuard;唯讀驗收時這些也要一起消失。
 * 有 perm 的入口不必包這個:`can()` 在唯讀模式已經把 create/update/delete 全擋了。
 *
 *   <WriteAction><Button onClick={openCreate}>新增機櫃</Button></WriteAction>
 */
export const WriteAction = ({ fallback = null, children }) =>
  (isReadOnlyMode() ? fallback : <>{children}</>);
WriteAction.propTypes = {
  fallback: PropTypes.node,
  children: PropTypes.node,
};

/**
 * 金額欄位遮罩：無金額檢視權者顯示 ***（主計劃 P0-5）。
 *
 *   <Money value={1042000} />  // 有權：NT$1,042,000 ／ 無權：***
 */
export const Money = ({ value, currency = "NT$", masked = "***" }) => {
  const { canSeeAmount } = useRbac();
  if (!canSeeAmount) return <span className="text-muted">{masked}</span>;
  const num = Number(value);
  const text = Number.isFinite(num) ? num.toLocaleString("zh-TW") : value;
  return <span>{currency}{text}</span>;
};
Money.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currency: PropTypes.string,
  masked: PropTypes.string,
};

/**
 * 開發用角色切換器（僅供 showcase / dev 驗證遮罩效果，正式版移除）。
 */
export const DevRoleSwitcher = () => {
  const { role, setRole } = useRbac();
  return (
    <div className="d-inline-flex align-items-center gap-2">
      <span className="text-muted small">目前角色</span>
      <Input
        type="select"
        bsSize="sm"
        style={{ width: 160 }}
        value={role}
        onChange={(e) => setRole(e.target.value)}
      >
        {Object.entries(ROLES).map(([key, r]) => (
          <option key={key} value={key}>{r.name}</option>
        ))}
      </Input>
    </div>
  );
};

export default RbacGuard;
